import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import { BULK_FILE_SKIP_CODES } from 'citadel-shared/fileManagement.js'
import { theme } from '../../themes/theme'
import { MUTED_TEXT_DARK } from '../styles/colors'
import { FlexColumn, FlexEndContainer, FlexRow, PaddedForm } from '../styles/Containers'
import BodyText, { BoldFormTitle, BoldMedBodyText, MedBodyText } from '../styles/Text'
import Button from '../common/Button'

// One friendly line per skip code of the bulk archive/move endpoints.
export const bulkFileSkipMessages = Object.freeze({
  [BULK_FILE_SKIP_CODES.NOT_FOUND]: 'File no longer exists',
  [BULK_FILE_SKIP_CODES.NOT_IN_CATEGORY]: 'File is not in this category anymore',
  [BULK_FILE_SKIP_CODES.ALREADY_ARCHIVED]: 'File was already archived',
  [BULK_FILE_SKIP_CODES.ALREADY_IN_CATEGORY]: 'File is already in that category',
  [BULK_FILE_SKIP_CODES.WRONG_FOI]: 'File belongs to a different request',
  [BULK_FILE_SKIP_CODES.STATUS_NOT_ALLOWED]: "File's category can't be changed right now"
})

// Sentence-case title for the bulk file dialogs.
export const FormTitle = styled(BoldFormTitle)`
  text-transform: none;
`

export const Helper = styled(MedBodyText)`
  color: ${MUTED_TEXT_DARK};
`

const SkippedRow = styled(FlexRow)`
  gap: 10px;
  align-items: center;
  padding: 10px 15px;
  border-radius: 8px;
  background: ${theme.colors.errorLight};

  .MuiSvgIcon-root {
    color: ${theme.colors.error};
    width: 20px;
    height: 20px;
  }
`

const SkippedReason = styled(MedBodyText)`
  color: ${MUTED_TEXT_DARK};
`

// Resolve a bulk endpoint's skipped entries ({id, code}) to displayable rows,
// looking filenames up in the submitted files and codes up in the shared map.
export function toSkippedRows(skipped, files, fallbackMessage) {
  const filenameById = new Map(files.map(f => [f.id, f.filename]))
  return skipped.map(s => ({
    id: s.id,
    filename: filenameById.get(s.id) ?? s.id,
    message: bulkFileSkipMessages[s.code] ?? fallbackMessage
  }))
}

// The shared result state of the bulk file dialogs: which files a partially
// successful batch skipped, and why. The dialog owns its copy (title, summary).
function BulkFileResultList({ title, summary, skipped, closeLabel, onClose = () => {} }) {
  return (
    <PaddedForm as="div">
      <FormTitle>{title}</FormTitle>
      <BodyText>{summary}</BodyText>
      <FlexColumn gap="10px">
        {skipped.map(s => (
          <SkippedRow key={s.id}>
            <ErrorOutlineIcon />
            <FlexColumn gap="2px">
              <BoldMedBodyText>{s.filename}</BoldMedBodyText>
              <SkippedReason>{s.message}</SkippedReason>
            </FlexColumn>
          </SkippedRow>
        ))}
      </FlexColumn>
      <FlexEndContainer>
        <Button onClick={onClose}>{closeLabel}</Button>
      </FlexEndContainer>
    </PaddedForm>
  )
}

BulkFileResultList.propTypes = {
  title: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  skipped: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired
    })
  ).isRequired,
  closeLabel: PropTypes.string.isRequired,
  onClose: PropTypes.func
}

export default BulkFileResultList
